// ==================== OFFLINE SUPPORT FOR WEB ====================

const CACHE_PREFIX = 'provaplus_cache_';
const QUEUE_KEY = 'provaplus_offline_queue';
const LAST_SYNC_KEY = 'provaplus_last_sync';

export interface OfflineQueueItem {
  id: string;
  action: 'create' | 'update' | 'delete';
  table: string;
  data: any;
  timestamp: string;
}

// ==================== CACHE ====================

export function cacheData(key: string, data: any): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
      data,
      cachedAt: new Date().toISOString(),
    }));
  } catch {
    // Storage full - clear old items
    clearOldCache();
  }
}

export function getCachedData<T>(key: string, maxAgeMs = 5 * 60 * 1000): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { data, cachedAt } = JSON.parse(raw);
    const age = Date.now() - new Date(cachedAt).getTime();
    if (age > maxAgeMs) return null;
    return data as T;
  } catch {
    return null;
  }
}

export function clearOldCache(): void {
  const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
  // Remove entries older than 1 hour
  keys.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const { cachedAt } = JSON.parse(raw);
      if (Date.now() - new Date(cachedAt).getTime() > 60 * 60 * 1000) {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  });
}

// ==================== OFFLINE QUEUE ====================

export function getOfflineQueue(): OfflineQueueItem[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addToOfflineQueue(item: Omit<OfflineQueueItem, 'id' | 'timestamp'>): void {
  const queue = getOfflineQueue();
  queue.push({
    ...item,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
  });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function removeFromOfflineQueue(id: string): void {
  const queue = getOfflineQueue().filter((item) => item.id !== id);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function clearOfflineQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
}

// ==================== CONNECTION STATUS ====================

export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export function onConnectionChange(callback: (online: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// ==================== SYNC ====================

export function getLastSyncTime(): string | null {
  return localStorage.getItem(LAST_SYNC_KEY);
}

export function setLastSyncTime(): void {
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
}

export function timeSinceLastSync(): string {
  const last = getLastSyncTime();
  if (!last) return 'Nunca sincronizado';

  const diff = Math.floor((Date.now() - new Date(last).getTime()) / 1000);
  if (diff < 60) return 'Agora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return new Date(last).toLocaleDateString('pt-BR');
}
